import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { authenticatedFetch, getApiUrl } from '@/utils/authService';
import { log } from '@/utils/vueLogger.js';
import { CacheManager } from '@/utils/cacheManager.js';

const distributionChannelCache = new CacheManager('VueCache', 'cache', {
	maxAge: 24 * 60 * 60 * 1000,
	memoryCacheTTL: 30 * 60 * 1000,
	logCategory: 'DistributionChannel',
	keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.distributionChannels.`,
});

export async function getDistributionChannels(forceRefresh = false) {
	if (!forceRefresh) {
		const memCached = distributionChannelCache.getFromMemory('all');
		if (memCached) return memCached;

		try {
			const legacyData = await iframeDataBridge.getStorageAsync('DISTRIBUTIONCHANNELS');
			if (legacyData) {
				log('info', 'DistributionChannel', '📦 Seeding Vue cache from legacy storage (DISTRIBUTIONCHANNELS)');
				await distributionChannelCache.put('all', legacyData);
				distributionChannelCache.setInMemory('all', legacyData);
				return legacyData;
			}
		} catch (legacyError) {
			log('warn', 'DistributionChannel', `⚠️ Legacy storage check failed: ${legacyError.message}`);
		}
	}

	try {
		const cached = await distributionChannelCache.get('all');
		if (cached && !forceRefresh) {
			distributionChannelCache.setInMemory('all', cached);

			if (!iframeDataBridge.isOnline()) {
				log('info', 'DistributionChannel', '📴 Offline: Returning cached distribution channels');
				return cached;
			}

			setTimeout(() => refreshDistributionChannelsInBackground(), 100);
			return cached;
		}

		if (!iframeDataBridge.isOnline()) {
			log('warn', 'DistributionChannel', '📴 Offline: No cached distribution channels available');
			return [];
		}

		return await fetchDistributionChannelsFromAPI();
	} catch (error) {
		log('error', 'DistributionChannel', `❌ Error getting distribution channels: ${error.message}`);

		try {
			const fallback = await distributionChannelCache.get('all');
			if (fallback) {
				log('info', 'DistributionChannel', '💾 Using fallback cached distribution channels');
				return fallback;
			}
		} catch (cacheError) {
			log('error', 'DistributionChannel', `❌ Cache fallback failed: ${cacheError.message}`);
		}

		return [];
	}
}

async function fetchDistributionChannelsFromAPI() {
	try {
		const response = await authenticatedFetch(getApiUrl('Sales/DistributionChannels'), { method: 'GET' }, 'tokenSales');

		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

		const distributionChannels = await response.json();
		await distributionChannelCache.put('all', distributionChannels);
		distributionChannelCache.setInMemory('all', distributionChannels);

		log('success', 'DistributionChannel', `✅ Fetched and cached ${distributionChannels.length} distribution channels`);
		return distributionChannels;
	} catch (error) {
		log('error', 'DistributionChannel', `❌ Failed to fetch distribution channels from API: ${error.message}`);
		throw error;
	}
}

async function refreshDistributionChannelsInBackground() {
	try {
		await fetchDistributionChannelsFromAPI();
		log('success', 'DistributionChannel', '🔄 Background refresh of distribution channels completed');
	} catch (error) {
		log('warn', 'DistributionChannel', `⚠️ Background refresh failed: ${error.message}`);
	}
}

export async function refreshDistributionChannels() {
	distributionChannelCache.clearMemory();
	await distributionChannelCache.clear();
}
